import { ColumnDef } from '@tanstack/react-table'
import React from 'react'
import { CourseAssignmentReport, CourseLessonsReport, CourseQuizReport, CourseTopicReport } from '../types/courseDetails'
import TableCheckbox from '../../../../common/checkbox/TableCheckbox'
import { HandleShowCloseModal } from '../../../../common/modal/HandleShowCloseModal'
import Button from '../../../../common/button/Button'
import MicroModal from 'micromodal'
import { useCourseReport } from '../context/CourseReportContext'
import { useUserReport } from '../context/UserReportContext'
import Link from '../../../../common/link/Link'

const CourseContentColumns = ({type, from}) => {
    MicroModal.init()

    const { setSelectedQuiz } = from === 'course-report' ? useCourseReport() : useUserReport()

    const lessonColumn = () => {
        return React.useMemo<ColumnDef<CourseLessonsReport>[]>(
            () => [
            {
                id: 'select',
                header: ({ table }) => (
                <TableCheckbox
                    {...{
                    checked: table.getIsAllRowsSelected(),
                    indeterminate: table.getIsSomeRowsSelected(),
                    onChange: table.getToggleAllRowsSelectedHandler(),
                    }}
                />
                ),
                cell: ({ row }) => (
                <div className="px-1">
                    <TableCheckbox
                    {...{
                        checked: row.getIsSelected(),
                        disabled: !row.getCanSelect(),
                        indeterminate: row.getIsSomeSelected(),
                        onChange: row.getToggleSelectedHandler(),
                    }}
                    />
                </div>
                ),
            },
            {
                id: 'columns',
                columns: [
                    {
                        id: 'lessonId',
                        accessorKey: 'lessonId',
                        prettyName: 'Lesson ID',
                        show: false,
                        cell: info => info.getValue(),
                        header: () => <span>LESSON ID</span>
                    },
                    {
                        id: 'lessonName',
                        accessorKey: 'lessonName',
                        prettyName: 'Lesson Name',
                        cell: info => info.getValue(),
                        footer: props => props.column.id,
                        header: () => <span>LESSON NAME</span>,
                    },
                    { 
                        id: 'lessonOrder',
                        accessorKey: 'lessonOrder',
                        prettyName: 'Lesson Order',
                        cell: info => info.getValue(),
                        header: () => <span>ORDER</span>,
                    },
                    { 
                        id: 'lessonStatus',
                        accessorKey: 'lessonStatus',
                        prettyName: 'Lesson Status',
                        cell: info => info.getValue(),
                        header: () => <span>STATUS</span>,
                    }
                ],
            },
            ],
        []
      )
    }

    const topicColumn =  () => {
        return React.useMemo<ColumnDef<CourseTopicReport>[]>(
            () => [
                {
                    id: 'select',
                    header: ({ table }) => (
                    <TableCheckbox
                        {...{
                        checked: table.getIsAllRowsSelected(),
                        indeterminate: table.getIsSomeRowsSelected(),
                        onChange: table.getToggleAllRowsSelectedHandler(),
                        }}
                    />
                    ),
                    cell: ({ row }) => (
                    <div className="px-1">
                        <TableCheckbox
                        {...{
                            checked: row.getIsSelected(),
                            disabled: !row.getCanSelect(),
                            indeterminate: row.getIsSomeSelected(),
                            onChange: row.getToggleSelectedHandler(),
                        }}
                        />
                    </div>
                    ),
                },
                {
                    id: 'columns',
                    columns: [
                        {
                            id: 'topicId',
                            accessorKey: 'topicId',
                            prettyName: 'Topic ID',
                            show: false,
                            cell: info => info.getValue(),
                            header: () => <span>Topic ID</span>
                        },
                        { 
                            id: 'topicOrder',
                            accessorKey: 'topicOrder',
                            prettyName: 'Topic Order',
                            cell: info => info.getValue(),
                            header: () => <span>ORDER</span>,
                        },
                        {
                            id: 'topicName',
                            accessorKey: 'topicName',
                            prettyName: 'Topic Name',
                            cell: info => info.getValue(),
                            footer: props => props.column.id,
                            header: () => <span>TOPIC NAME</span>,
                        },
                        { 
                            id: 'topicStatus',
                            accessorKey: 'topicStatus',
                            prettyName: 'Topic Status',
                            cell: info => info.getValue(),
                            header: () => <span>STATUS</span>,
                        },
                        { 
                            id: 'topicAssociatedLesson',
                            accessorKey: 'topicAssociatedLesson',
                            prettyName: 'Associated Lesson',
                            cell: info => info.getValue(),
                            header: () => <span>ASSOCIATED LESSON</span>,
                        },
                    ],
                },
            ],
        []
      )
    }

    const quizColumn = () => {
        return React.useMemo<ColumnDef<CourseQuizReport>[]>(
            () => [
                {
                    id: 'select',
                    header: ({ table }) => (
                    <TableCheckbox
                        {...{
                        checked: table.getIsAllRowsSelected(),
                        indeterminate: table.getIsSomeRowsSelected(),
                        onChange: table.getToggleAllRowsSelectedHandler(),
                        }}
                    />
                    ),
                    cell: ({ row }) => (
                    <div className="px-1">
                        <TableCheckbox
                        {...{
                            checked: row.getIsSelected(),
                            disabled: !row.getCanSelect(),
                            indeterminate: row.getIsSomeSelected(),
                            onChange: row.getToggleSelectedHandler(),
                        }}
                        />
                    </div>
                    ),
                },
                {
                    id: 'columns',
                    columns: [
                        {
                            id: 'quizId',
                            accessorKey: 'quizId',
                            show: false,
                            cell: info => info.getValue(),
                            header: () => <span>QUIZ ID</span>
                        },
                        {
                            id: 'quizProId',
                            accessorKey: 'quizProId',
                            show: false,
                            cell: info => info.getValue(),
                            header: () => <span>PRO QUIZ ID</span>
                        },
                        {
                            id: 'statisticRefId',
                            accessorKey: 'statisticRefId',
                            show: false,
                            cell: info => info.getValue(),
                            header: () => <span>STATS REF ID</span>
                        },
                        {
                            id: 'quizName',
                            accessorKey: 'quizName',
                            cell: info => info.getValue(),
                            footer: props => props.column.id,
                            header: () => <span>QUIZ NAME</span>,
                        },
                        { 
                            id: 'quizScore',
                            accessorKey: 'quizScore',
                            cell: info => info.getValue(),
                            header: () => <span>SCORE</span>,
                        },
                        { 
                            id: 'dateCompleted',
                            accessorKey: 'dateCompleted',
                            cell: info => info.getValue() ?? '-',
                            header: () => <span>DATE COMPLETED</span>,
                        },
                        { 
                            id: 'viewQuizDetails',
                            accessorKey: 'viewQuizDetails',
                            prettyName: 'Details',
                            cell: (info) => (
                              <QuizViewDetails info={info.row.original} />
                            ),
                            header: () => <span>DETAILED REPORT</span>,
                        },
                        { 
                            id: 'quizCertificateLink',
                            accessorKey: 'quizCertificateLink',
                            prettyName: 'Certificate',
                            cell: (info) => (
                              <DownloadButton info={info.row.original} />
                            ),
                            header: () => <span>CERTIFICATE</span>,
                        },
                    ],
                },
            ],
        []
      )
    }

    const assignmentColumn = () => {
         return React.useMemo<ColumnDef<CourseAssignmentReport>[]>(
            () => [
                {
                    id: 'select',
                    header: ({ table }) => (
                    <TableCheckbox
                        {...{
                        checked: table.getIsAllRowsSelected(),
                        indeterminate: table.getIsSomeRowsSelected(),
                        onChange: table.getToggleAllRowsSelectedHandler(),
                        }}
                    />
                    ),
                    cell: ({ row }) => (
                    <div className="px-1">
                        <TableCheckbox
                        {...{
                            checked: row.getIsSelected(),
                            disabled: !row.getCanSelect(),
                            indeterminate: row.getIsSomeSelected(),
                            onChange: row.getToggleSelectedHandler(),
                        }}
                        />
                    </div>
                    ),
                },
                {
                    id: 'columns',
                    columns: [
                        {
                            id: 'lessonId',
                            accessorKey: 'lessonId',
                            show: false,
                            cell: info => info.getValue(),
                            header: () => <span>ASSIGNMENT ID</span>
                        },
                        {
                            id: 'assignmentName',
                            accessorKey: 'assignmentName',
                            cell: info => info.getValue(),
                            footer: props => props.column.id,
                            header: () => <span>ASSIGNMENT NAME</span>,
                        },
                        { 
                            id: 'assignmentApproval',
                            accessorKey: 'assignmentApproval',
                            cell: info => info.getValue(),
                            header: () => <span>APPROVAL STATUS</span>,
                        },
                        { 
                            id: 'assignmentPoints',
                            accessorKey: 'assignmentPoints',
                            cell: info => info.getValue() ?? '-',
                            header: () => <span>POINTS</span>,
                        },
                        { 
                            id: 'assignmentSubmittedDate',
                            accessorKey: 'assignmentSubmittedDate',
                            cell: info => info.getValue(),
                            header: () => <span>SUBMITTED ON</span>,
                        },
                        { 
                            id: 'assignmentDownloadLink',
                            accessorKey: 'assignmentDownloadLink',
                            prettyName: 'Assignment Link',
                            cell: (info) => (
                                <DownloadButton info={info.row.original} />
                            ),
                            header: () => <span>ACTIONS</span>,
                        },
                    ],
                },
            ],
        []
      )
    }

    const handleViewQuiz = (info) => {
        setSelectedQuiz(info)
        HandleShowCloseModal( 'msync-report-user-view-quiz', 'msync-report-user-container' )
    }

    const QuizViewDetails = ({info}) => {
      return (
        <Button
          name='View Details' 
          className='msync-button--is-size-sm msync-button--is-theme-secondary msync-button--is-style-outline' 
          onClick={()=>handleViewQuiz(info)}
        />
      )
    }

    const DownloadButton = ({info}) => {

        const isQuiz = info.hasOwnProperty('quizCertificateLink')

        const link = isQuiz ? info.quizCertificateLink ?? '' : info.assignmentDownloadLink ?? ''

        const isEmpty = link === ''

        return (
            <Link 
              className={`msync-button msync-button--is-size-sm msync-button--is-theme-secondary msync-button--is-style-outline`} 
              link={link === "" ? false : link }
              target={'_blank'}
              disabled={link === "" && true }
            >
                Download
            </Link>
        )
    }

    switch( type ){
       case 'lessons':
            return lessonColumn()
        case 'topics':
            return topicColumn()
        case 'quizzes': 
            return quizColumn()
        case 'assignments':
            return assignmentColumn()
    }
}

export default CourseContentColumns
